import { EmbarkCart, EmbarkProduct } from 'shopify-buy';
import { useEffect, useMemo, useState } from 'preact/hooks';
import React from 'preact/compat';
import { fetchGraphQL } from '../storefront-cart';
import { getRecommendations } from './upsellRecommendation';
import { notifyHandledException } from '../../utils/errorLogger';
import { ProductVariant } from 'shopify-buy';
import { Recommendation } from './Recommendation';
import { toShopifyGlobalId } from '../storefront-cart';

interface RecommendationsProps {
  cart: EmbarkCart;
  onChange?: () => void;
}

const FETCH_PRODUCT_BY_HANDLE_QUERY = `
  query($handle: String!) {
    productByHandle(handle: $handle) {
      id
      title
      handle
      featuredImage {
        id
        src
      }
      displayPrice: metafield(namespace: "meta", key: "display_price") {
        value
        type
      }
      variants(first: 25) {
        edges {
          node {
            id
            title
            sku
            price {
              amount
            }
            compareAtPrice {
              amount
            }
            image {
              id
              src
            }
            displayPrice: metafield(namespace: "meta", key: "display_price") {
              value
              type
            }
          }
        }
      }
    }
  }
`;

export const Recommendations: React.FC<RecommendationsProps> = ({
  cart,
  onChange,
}) => {
  const [recommended, setRecommended] = useState<
    Array<{
      product: EmbarkProduct;
      variant: ProductVariant;
      variant_id: number;
      sellingPlanId?: string;
      discountCode?: string;
    }>
  >([]);

  const recommendations = useMemo(
    () => getRecommendations(cart),
    [cart?.lines]
  );

  useEffect(() => {
    async function getRecommendedProducts(): Promise<void> {
      const recommendedProducts = await Promise.all(
        recommendations.map(async (recommendation) => {
          // Fetch product by handle
          const productResponse = await fetchGraphQL(
            FETCH_PRODUCT_BY_HANDLE_QUERY,
            {
              handle: recommendation.product,
            }
          );
          const product = productResponse.productByHandle;

          const [variant] =
            product?.variants.edges?.filter(
              (v: any) =>
                v.node.id ===
                toShopifyGlobalId(recommendation.variant.toString())
            ) || [];

          return {
            product,
            variant_id: recommendation.variant,
            variant,
            sellingPlanId: recommendation.sellingPlanId?.toString(),
            discountCode: recommendation.discountCode,
          };
        })
      );

      setRecommended(recommendedProducts);
    }

    if (!cart || !recommendations || recommendations.length === 0) {
      setRecommended([]);
    }

    getRecommendedProducts().catch(notifyHandledException);
  }, [cart?.lines]);

  if (!cart || !recommended?.length) {
    return null;
  }

  return (
    <div className={'recommendations'}>
      <h4 className={'recommendations__title'}>Recommended products</h4>

      <ul className={'recommendations__items'}>
        {recommended.map(
          ({ product, variant, sellingPlanId, discountCode }) => {
            return (
              <Recommendation
                product={product}
                variant={variant}
                sellingPlanId={sellingPlanId}
                discountCode={discountCode}
                onChange={onChange}
              />
            );
          }
        )}
      </ul>
    </div>
  );
};
